/**
 * @Author Morsal
 * @Author Manal = resultOverlay function stopwatch "GAME OVER"
 */
import { StopwatchService } from "../services/StopwatchService";
import { GetUsernameResponse, UsernameService } from "../services/UsernameService";

export class StopwatchComponent extends HTMLElement {
    private _startTime: number | null = null;
    private _intervalId: number | null = null;
    private _elapsedOffset: number = 0;
    private _isPaused: boolean = false;
    private _pauseStartedAt: number | null = null;
    public _isStopped: boolean = false;
    private _display!: HTMLSpanElement;
    private _pauseButton!: HTMLButtonElement;
    private _overlay!: HTMLDivElement;
    private _resultOverlay!: HTMLDivElement;

    private _stopwatchService = new StopwatchService();

    public constructor() {
        super();
        this.attachShadow({ mode: "open" });

        const style: HTMLStyleElement = document.createElement("style");
        style.textContent = `
            .container {
                display: flex;
                align-items: center;
                gap: 10px;
                font-family: "Orbitron", sans-serif;
                color: #cceeff;
                text-shadow: 0 0 4px #00ffff, 0 0 8px #00ffff;
            }

            .button {
                background-color: rgba(0, 0, 0, 0.8);
                border: 2px solid #ff00ff;
                padding: 5px 10px;
                font-size: 0.8rem;
                color: #ff00ff;
                text-shadow: 0 0 5px #ff00ff;
                cursor: pointer;
                border-radius: 5px;
                transition: 0.3s;
            }

            .button:hover {
                background-color: #ff00ff;
                color: black;
                box-shadow: 0 0 10px #ff00ff;
            }

            .overlay, .result-overlay {
                position: fixed;
                top: 0;
                left: 0;
                width: 100vw;
                height: 100vh;
                background: rgba(0, 0, 20, 0.9);
                display: flex;
                justify-content: center;
                align-items: center;
                z-index: 9999;
                backdrop-filter: blur(4px);
                animation: fadeIn 0.3s ease-in-out;
            }

            .popup-content, .result-content {
                background-color: rgba(10, 10, 30, 0.95);
                padding: 40px;
                border: 2px solid #00eaff;
                border-radius: 16px;
                box-shadow: 0 0 25px #00eaff77;
                display: flex;
                flex-direction: column;
                align-items: center;
                animation: popupZoom 0.4s ease-out;
                gap: 20px;
            }

            .popup-content h1, .result-content h1 {
                font-size: 2.5rem;
                color: #00eaff;
                text-shadow: 0 0 10px #00eaff, 0 0 20px #00eaff;
                text-align: center;
                animation: flicker 2s infinite alternate;
            }

            .hidden {
                display: none !important;
            }

            .pause-icon {
                width: 65px;
                height: 40px;
                background-color: #1a001f;
                border: 2px solid #ff00ff;
                border-radius: 10px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: relative;
                cursor: pointer;
                box-shadow: 0 0 10px #ff00ff88;
                transition: all 0.3s ease;
            }

            .pause-icon::before,
            .pause-icon::after {
                content: "";
                display: block;
                width: 8px;
                height: 28px;
                background-color: #ff00ff;
                border-radius: 2px;
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                box-shadow: 0 0 6px #ff00ff, 0 0 12px #ff00ff;
            }

            .pause-icon::before {
                left: 18px;
            }

            .pause-icon::after {
                left: 34px;
            }

            .pause-icon:hover {
                background-color: #2a002f;
                box-shadow:
                0 0 15px #ff00ff,
                0 0 30px #ff00ff,
                0 0 45px #ff00ff;
            }

            .stopped {
                opacity: 0.5;
                text-shadow: none;
            }

            @keyframes popupZoom {
                0% {
                    opacity: 0;
                    transform: scale(0.8);
                }
                100% {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }

            @keyframes flicker {
                0% { opacity: 1; }
                50% { opacity: 0.8; }
                100% { opacity: 1; }
            }

            @media (max-width: 768px) {
                .popup-content h1 {
                    font-size: 2rem;
                }
                .popup-content .button {
                    font-size: 0.9rem;
                    padding: 10px 20px;
                }
                .popup-content > div {
                    flex-direction: column !important;
                    align-items: stretch;
                }
            }
        `;

        const container: HTMLDivElement = document.createElement("div");
        container.classList.add("container");

        this._display = document.createElement("span");
        this._display.textContent = "00:00:00";

        const usernameSpan: HTMLSpanElement = document.createElement("span");
        usernameSpan.id = "username";
        usernameSpan.style.marginRight = "20px";
        usernameSpan.textContent = "";
        container.appendChild(usernameSpan);

        this._pauseButton = document.createElement("button");
        this._pauseButton.className = "pause-icon";
        this._pauseButton.title = "Pause";
        this._pauseButton.addEventListener("click", () => this.togglePause());

        if (this._isStopped) {
            this._pauseButton.style.display = "none";
        }

        this._overlay = document.createElement("div");
        this._overlay.className = "overlay hidden";

        const popupContent: HTMLDivElement = document.createElement("div");
        popupContent.className = "popup-content";

        const pausedText: HTMLHeadingElement = document.createElement("h1");
        pausedText.textContent = "PAUSED";

        const resumeButton: HTMLButtonElement = document.createElement("button");
        resumeButton.textContent = "Resume Game";
        resumeButton.className = "button";
        resumeButton.addEventListener("click", () => this.resume());

        const quitButton: HTMLButtonElement = document.createElement("button");
        quitButton.textContent = "Quit Game";
        quitButton.className = "button";
        quitButton.addEventListener("click", () => this.quit());

        popupContent.appendChild(pausedText);
        popupContent.appendChild(resumeButton);
        popupContent.appendChild(quitButton);

        this._overlay.appendChild(popupContent);

        this._resultOverlay = document.createElement("div");
        this._resultOverlay.className = "result-overlay hidden";
        const resultContent: HTMLDivElement = document.createElement("div");
        resultContent.className = "result-content";
        resultContent.innerHTML = "<h1>Game Over!<br>Your playtime: <span id='final-time'></span></h1>";
        this._resultOverlay.appendChild(resultContent);

        container.appendChild(this._display);
        container.appendChild(this._pauseButton);

        this.shadowRoot!.appendChild(style);
        this.shadowRoot!.appendChild(container);
        this.shadowRoot!.appendChild(this._overlay);
        this.shadowRoot!.appendChild(this._resultOverlay);

        void this.loadUsername();
    }

    public connectedCallback(): void {
        void this.setFromBackend();
        this.stopTimer();
    }

    public async startTimerFromBackend(): Promise<void> {
        if (this._intervalId !== null || this._startTime !== null) return;
        try {
            const { startTime } = await this._stopwatchService.startGame();
            this._startTime = startTime;
            this.startInterval();
        }
        catch (e) {
            console.error("Kon starttijd niet ophalen:", e);
        }
    }

    private startInterval(): void {
        if (this._isStopped) return;

        this.updateDisplay(); // toon direct de juiste tijd

        this._intervalId = window.setInterval(() => {
            if (this._startTime !== null && !this._isPaused) {
                this.updateDisplay(); // elke seconde updaten
            }
        }, 1000);
    }

    private stopTimer(): void {
        if (this._intervalId !== null) {
            clearInterval(this._intervalId);
            this._intervalId = null;
        }
    }

    public async quit(): Promise<void> {
        try {
            const result: { message: string; endTime: number; totalTime: number } = await this._stopwatchService.quitGame();

            this.stopTimer();
            this._overlay.classList.add("hidden");
            this._isStopped = true;
            const timeText: string = this.formatTime(result.totalTime);
            const timeSpan: Element | null = this._resultOverlay.querySelector("#final-time");
            if (timeSpan) timeSpan.textContent = timeText;
            this._resultOverlay.classList.remove("hidden");
            setTimeout(() => {
                this._resultOverlay.classList.add("hidden");
            }, 5000);
        }
        catch (e) {
            console.error("Failed to quit game:", e);
        }
    }

    private togglePause(): void {
        if (this._isPaused) {
            void this.resume();
        }
        else {
            void this.pause();
        }
    }

    public async pause(): Promise<void> {
        if (!this._isPaused && this._startTime !== null) {
            this._isPaused = true;
            this._pauseStartedAt = Date.now();
            this._overlay.classList.remove("hidden");
            try {
                await this._stopwatchService.pauseGame();
            }
            catch (e) {
                console.error("Pause failed", e);
            }
        }
    }

    public async resume(): Promise<void> {
        if (this._isStopped) return;
        if (this._isPaused && this._startTime !== null && this._pauseStartedAt !== null) {
            this._isPaused = false;
            const pauseDuration: number = Date.now() - this._pauseStartedAt;
            this._elapsedOffset += pauseDuration;
            this._pauseStartedAt = null;
            this._overlay.classList.add("hidden");
            try {
                await this._stopwatchService.resumeGame();
            }
            catch (e) {
                console.error("Resume failed", e);
            }
        }
    }

    private formatTime(ms: number): string {
        const sec: number = Math.floor(ms / 1000) % 60;
        const min: number = Math.floor(ms / 60000) % 60;
        const hr: number = Math.floor(ms / 3600000);
        return `${hr}:${min.toString().padStart(2, "0")}:${sec.toString().padStart(2, "0")}`;
    }

    public isRunning(): boolean {
        return this._intervalId !== null && !this._isPaused;
    }

    public isStopped(): boolean {
        return this._isStopped;
    }

    public async resumeAfterRender(): Promise<void> {
        try {
            const { startTime, isPaused } = await this._stopwatchService.getStatus();
            if (startTime && !this._isStopped) {
                this._startTime = startTime;
                this._isPaused = isPaused;
                if (!this._isPaused) {
                    this.startInterval();
                }
            }
        }
        catch (e) {
            console.error("Kon stopwatch status niet ophalen", e);
        }
    }

    public async setFromBackend(): Promise<void> {
        try {
            const { startTime, isPaused, isStopped } = await this._stopwatchService.getStatus();
            this._isStopped = isStopped;
            if (isStopped) {
                this.stopTimer();
                this.updateDisplay();
                this._pauseButton.style.display = "none";
                const timeText: string = this.formatTime(Date.now() - (startTime ?? Date.now()));
                const timeSpan: Element | null = this._resultOverlay.querySelector("#final-time");
                if (timeSpan) timeSpan.textContent = timeText;
                this._resultOverlay.classList.remove("hidden");
                setTimeout(() => {
                    this._resultOverlay.classList.add("hidden");
                }, 5000);
                return;
            }
            if (startTime) {
                this._startTime = startTime;
                this._isPaused = isPaused;
                this._elapsedOffset = 0;
                if (!isPaused) {
                    this.startInterval();
                }
                this.updateDisplay();
            }
            void this.loadUsername();
        }
        catch (e) {
            console.error("Kon stopwatch status niet ophalen:", e);
        }
    }

    public updateDisplay(): void {
        if (this._isStopped || this._startTime === null) return;

        const now: number = Date.now();
        const elapsed: number = now - this._startTime - this._elapsedOffset;
        this._display.textContent = this.formatTime(elapsed);
    }

    private async loadUsername(): Promise<void> {
        try {
            const usernameResponse: GetUsernameResponse = await new UsernameService().getUsername();
            const username: string | null = usernameResponse.username;
            if (this.shadowRoot) {
                const nameEl: Element | null = this.shadowRoot.querySelector("#username");
                if (nameEl && username) {
                    nameEl.textContent = username;
                }
            }
        }
        catch (e) {
            console.error("Kon gebruikersnaam niet ophalen:", e);
        }
    }
}
